import type { FC } from 'react'
import {
	Heading,
	Box,
	Button,
	Text,
	ChakraProps,
	Grid,
	useDisclosure,
	Flex,
	Tooltip,
} from '@chakra-ui/react'
import {
	Feature,
	ContentSection,
	ContentSectionHeading,
} from '@alexanderathoodly/ui-library'
import type { ServiceOfficeMembershipResult } from '@alexanderathoodly/data-models'
import { formatInt } from '@alexanderathoodly/data-models'
import { useLocationPage } from './context'
import { truncate } from '~/utils/text'
import { RequestShowingButton } from '~/forms/showingrequestform'
import { useUser } from 'reactfire'
import {
	ShowPriceButton,
	ShowPriceModal,
	useShowPriceModal,
} from '~/forms/showpriceform'
import { InfoAboutBlurredPrice } from './solutions'
import { sendGA4Event } from '~/analytics/ga'
import { useTranslation } from 'react-i18next'
import { useMemberships } from './utils'
import { useAIBot } from '~/aibotcontext'

const MEMBERSHIP_MAP = {
	fixed_desk: 'Fixed desk',
	flex_desk: 'Flex desk',
	only_access: 'Lounge',
}

const MembershipItem: FC<
	{
		membership: ServiceOfficeMembershipResult
		isLast: boolean
		showPrices: boolean
	} & ChakraProps
> = ({ membership, isLast, showPrices, ...rest }) => {
	const { t, i18n } = useTranslation()
	const { isOpen, onToggle } = useDisclosure()
	const { isShowPriceModalOpen, onShowPriceModalClose, onShowPriceModalOpen } =
		useShowPriceModal('membership', membership.id)

	const MAX_DESCRIPTION_LENGTH = 80
	const { location } = useLocationPage()
	const membershipDescription =
		i18n.language != 'en' ? membership.description : membership.description_en
	return (
		<>
			<Grid
				as="article"
				templateRows={{ base: 'repeat(4,auto)', md: 'auto' }}
				templateColumns={{ base: 'auto', md: 'min-content auto min-content' }}
				columnGap={3}
				rowGap={3}
				borderColor="grey.100"
				borderStyle="solid"
				borderBottomWidth={isLast ? 0 : 'thin'}
				pb={3}
				{...rest}
			>
				<Heading
					as="h3"
					fontSize="md"
					fontWeight="semibold"
					gridColumn={{ base: 'auto', md: '1/3' }}
					gridRow={{ base: 'auto', md: '1/2' }}
				>
					{membership.name}
				</Heading>
				{showPrices ? (
					<Feature
						name={t('Kostnad')}
						value={`${formatInt(membership.price)}kr/person/mån`}
					/>
				) : (
					<>
						<Tooltip
							label={<InfoAboutBlurredPrice />}
							maxW="max-content"
							borderRadius="5px"
							py={1}
						>
							<Flex
								gap="4px"
								gridColumn={{ base: 'auto', md: '1/2' }}
								gridRow={{ base: 'auto', md: '2/3' }}
							>
								<Text as="dt" color="grey.500">
									{t('Kostnad')}:
								</Text>
								<Text
									as="dd"
									fontWeight="semibold"
									color="grey.800"
									filter="blur(4px)"
									cursor="pointer"
									onClick={() => {
										onShowPriceModalOpen()
										sendGA4Event('initiate_price_account', {
											step: 'initiate',
											location: 'blurred price',
										})
									}}
								>
									12345
								</Text>
								<Text>{t('kr/person/mån')}</Text>
							</Flex>
						</Tooltip>
					</>
				)}
				<Feature
					name="Typ"
					value={MEMBERSHIP_MAP[membership.desk_type]}
					gridColumn={{ base: 'auto', md: '2/3' }}
					gridRow={{ base: 'auto', md: '2/3' }}
				/>
				<Text
					gridColumn={{ base: 'auto', md: '1/3' }}
					gridRow={{ base: 'auto', md: '3/4' }}
					maxWidth={{ base: '100%', md: '80%' }}
				>
					{isOpen
						? membershipDescription
						: truncate(membershipDescription, MAX_DESCRIPTION_LENGTH)}
				</Text>
				{showPrices ? (
					<RequestShowingButton
						membership={membership}
						display="inline-flex"
						variant="outline"
						gridColumn={{ base: 'auto', md: '3/4' }}
						gridRow={{ base: 'auto', md: '1/3' }}
					/>
				) : (
					location && (
						<ShowPriceButton
							GA4EventLocation="membership"
							onClick={onShowPriceModalOpen}
						>
							{t('Se pris')}
						</ShowPriceButton>
					)
				)}
				{membershipDescription.length > MAX_DESCRIPTION_LENGTH && (
					<Button
						variant="link"
						color="blue.500"
						size="sm"
						onClick={() => {
							onToggle()
						}}
						gridColumn={{ base: 'auto', md: '3/4' }}
						gridRow={{ base: '5/6', md: '3/4' }}
						justifySelf="end"
						width={{
							base: '100%',
							md: 'min-content',
						}}
					>
						{isOpen ? 'Visa mindre' : 'Visa mer'}
					</Button>
				)}
			</Grid>
			{location && (
				<ShowPriceModal
					location={location}
					membership={membership}
					isOpen={isShowPriceModalOpen}
					onClose={onShowPriceModalClose}
				/>
			)}
		</>
	)
}

export const MembershipSection: FC<{ locationName: string }> = ({
	locationName,
	...rest
}) => {
	const { t } = useTranslation()
	const { location } = useLocationPage()
	const { data: user } = useUser()
	const { isAIBot } = useAIBot()
	const showPrices = isAIBot || !!user
	const memberships = useMemberships(location?.id)
	if (memberships.isError || memberships.data?.length == 0) {
		return null
	}
	return (
		<ContentSection id="membership" {...rest}>
			<ContentSectionHeading>{t('Medlemskap')}</ContentSectionHeading>
			<Text>
				{memberships.data
					? t(
							'Medlemskap som erbjuds på {{location_name}}. Samtliga medlemskap säljs per månad. Hyrs antingen självständigt eller i kombination med kontorsrum som extramedlemskap vid behov av mer kapacitet.',
							{ location_name: locationName }
					  )
					: t(
							'Det finns inga tillgängliga medlemskap på {{location_name}} just nu.',
							{ location_name: locationName }
					  )}
			</Text>
			{memberships.data && (
				<Box mt={3}>
					{memberships.data.map((membership, index) => (
						<MembershipItem
							key={membership.id}
							membership={membership}
							isLast={index + 1 == memberships.data.length}
							mb={5}
							showPrices={showPrices}
						/>
					))}
				</Box>
			)}
		</ContentSection>
	)
}
